import React, { FC, useEffect, useState } from 'react'
import { groupBy } from 'lodash'

import { useAppSelector } from '../../../../store/hooks'
import { selectFeesTabDataR } from '../../../../store/registration/registrationSlice'
import { selectedEvent } from '../../../../store/events/eventsSlice'

import CompetitorEventRegisterWrapper from '../../CompetitorEventRegisterWrapper'
import FeesItem from '../../event-registration-tabs/components/fees/FeesItem'
import DataNotAvailable from '../../../../components/common/alerts/data-not-available/DataNotAvailable'
import { Skeleton } from '../../../../components/ui/skeleton'

import { getCurrency } from '../../../../helpers/payment'

import RegistrationFeesService from '../../../../services/firebase-service/registration-fees/RegistrationFeesService'

import { IRegistrationFeesInterface } from '../../../../models/registration-fees/registrationFees.interface'

import { FEES_CATEGORY_CONST } from '../../../../components/events/views/details/EventDetailsViewComponentFees'

type CompetitorEventRegisterFeesTabProps = {
  title: string
  description: string
  eventId: string
}

const CompetitorEventRegisterFeesTab: FC<CompetitorEventRegisterFeesTabProps> = ({
  title,
  description,
  eventId,
}) => {
  const feesTabData = useAppSelector(selectFeesTabDataR)
  const selectedEventR = useAppSelector(selectedEvent)
  const [loading, setLoading] = useState(false)
  const [mainLoading, setMainLoading] = useState(false)
  const [soldFeesById, setSoldFeesById] = useState<{
    [key: string]: IRegistrationFeesInterface[]
  }>({})

  useEffect(() => {
    let selectedUnitsCount = 0
    let feesStateInHookForm: Record<string, number> = {}

    feesTabData?.forEach((currFeesRow) => {
      if (currFeesRow.registrationFees) {
        selectedUnitsCount = currFeesRow?.registrationFees?.selectedUnitsCount ?? 0
        feesStateInHookForm = {
          ...feesStateInHookForm,
          [currFeesRow.feesItemId]: selectedUnitsCount,
        }
      }
    })
  }, [feesTabData])

  useEffect(() => {
    const getFeesInDb = async () => {
      try {
        setMainLoading(true)
        const eventFeesInDb = await RegistrationFeesService.getByEventId(eventId)
        const grouped = groupBy([...eventFeesInDb], 'feesItemId')
        setSoldFeesById(grouped)
      } catch (error) {
        console.error('error: ', error)
      } finally {
        setMainLoading(false)
      }
    }
    getFeesInDb().then()
  }, [eventId])

  if (!feesTabData?.length) {
    return (
      <DataNotAvailable
        mode="text"
        containerClassName="text-SeabiscuitDark200ThemeColor text-nr 2xl:text-base"
        text="No fees items have been added to this event"
      />
    )
  }

  const currency = getCurrency(selectedEventR?.EventPaymentSettings?.currency)

  if (mainLoading) {
    return (
      <CompetitorEventRegisterWrapper title={title} description={description} className="mt-0">
        <div id="rtc" className="flex flex-col gap-3">
          {Array.from({ length: 2 }).map((_, index) => (
            <div key={index} className="border border-black/10 rounded-xl p-4 sm:p-6">
              <Skeleton className="h-7 w-1/2 sm:w-1/4 mb-2" />
              {Array.from({ length: 2 }).map((_, index) => (
                <div
                  key={index}
                  className="flex flex-wrap items-center gap-y-2 justify-between w-full py-4"
                >
                  <div className="w-full sm:w-1/2">
                    <Skeleton className="h-5 w-1/4" />
                    <Skeleton className="h-4 w-1/2 mt-1" />
                  </div>
                  <div className="w-full sm:w-1/2">
                    <Skeleton className="ml-auto h-10 w-full sm:w-[160px]" />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </CompetitorEventRegisterWrapper>
    )
  }

  return (
    feesTabData && (
      <CompetitorEventRegisterWrapper title={title} description={description}>
        <div id="rtc" className="flex flex-col gap-3">
          <FeesItem
            loading={loading}
            setLoading={setLoading}
            feesTabData={feesTabData}
            feesCategory={FEES_CATEGORY_CONST.STALLS}
            currency={currency}
            soldFeesById={soldFeesById}
          />
          <FeesItem
            loading={loading}
            setLoading={setLoading}
            feesTabData={feesTabData}
            feesCategory={FEES_CATEGORY_CONST.BEDDING}
            currency={currency}
            soldFeesById={soldFeesById}
          />
          <FeesItem
            loading={loading}
            setLoading={setLoading}
            feesTabData={feesTabData}
            feesCategory={FEES_CATEGORY_CONST.FEED}
            currency={currency}
            soldFeesById={soldFeesById}
          />
          <FeesItem
            loading={loading}
            setLoading={setLoading}
            feesTabData={feesTabData}
            feesCategory={FEES_CATEGORY_CONST.GOVERNANCE}
            currency={currency}
            soldFeesById={soldFeesById}
          />
          <FeesItem
            loading={loading}
            setLoading={setLoading}
            feesTabData={feesTabData}
            feesCategory={FEES_CATEGORY_CONST.OTHER}
            currency={currency}
            soldFeesById={soldFeesById}
          />
        </div>
      </CompetitorEventRegisterWrapper>
    )
  )
}

export default CompetitorEventRegisterFeesTab
